import React from "react";
import Button from "../../../components/buttons/Button";
import TreatmentElapsedTimer, { isTreatmentRunning } from "../../../components/Hud/TreatmentElapsedTimer";

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
};

const formatDuration = (start, end) => {
    if (!start || !end) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (isNaN(ms) || ms < 0) return null;
    const totalSeconds = Math.round(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const statusColor = (status) => {
    if (status === "Completed") return "bg-green-100 text-green-700 border-green-400";
    if (status === "In Progress") return "bg-yellow-100 text-yellow-800 border-yellow-400";
    return "bg-gray-100 text-gray-700 border-gray-300";
};

// Treatment controls for the latest dose of a TPT package on a center with a new TPT machine.
const TptTreatmentPanel = ({ lastDose, doseNumber, machineName, starting, waitingForMachine, onStart, refreshing, onRefresh }) => {
    if (!lastDose) return null;

    const hasStarted = Boolean(lastDose.open_time);
    const duration = formatDuration(lastDose.open_time, lastDose.close_time);
    const status = lastDose.status || "Not Started";

    return (
        <div className="mt-4 border border-primary/40 rounded-lg p-4 bg-slate-50">
            <div className="flex flex-row flex-wrap justify-between items-center gap-3">
                <div className="flex flex-row items-center gap-2">
                    <p className="font-bold text-sm">
                        TPT Treatment — Dose {doseNumber}
                        {machineName ? <span className="font-normal text-gray-500"> ({machineName})</span> : null}
                    </p>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusColor(status)}`}>
                        {status}
                    </span>
                </div>
                <div className="flex flex-row items-center gap-2">
                    {onRefresh && (
                        <Button
                            type="button"
                            className="bg-white border border-primary text-primary text-xs flex flex-row items-center gap-2"
                            onClick={refreshing ? undefined : onRefresh}
                            disabled={refreshing}
                        >
                            <span className={`inline-block ${refreshing ? "animate-spin" : ""}`}>&#x21bb;</span>
                            {refreshing ? "Refreshing..." : "Refresh"}
                        </Button>
                    )}
                    {!hasStarted && (
                        <Button
                            type="button"
                            className="bg-green-600 text-white text-xs flex flex-row items-center gap-2"
                            onClick={starting ? undefined : onStart}
                            disabled={starting}
                        >
                            {starting && (
                                <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            {starting ? "Starting..." : "Start Treatment"}
                        </Button>
                    )}
                </div>
            </div>

            {!hasStarted && waitingForMachine && (
                <p className="text-xs text-primary font-semibold mt-2">
                    Machine unlocked — waiting for the patient to start treatment...
                </p>
            )}

            {isTreatmentRunning(lastDose) && (
                <div className="mt-3">
                    <TreatmentElapsedTimer startTime={lastDose.open_time} />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs">
                <p><span className="font-bold">Started at:</span> {formatDateTime(lastDose.open_time)}</p>
                <p><span className="font-bold">Ended at:</span> {formatDateTime(lastDose.close_time)}</p>
                <p><span className="font-bold">Duration:</span> {duration ?? "—"}</p>
            </div>
        </div>
    );
};

export default TptTreatmentPanel;
